/* The spot in words.
   The tree already says which chart this is in shorthand, and the matrix already says what
   the range is. What neither says is what happened at the table, so the panel under the
   tree tells it: one row per beat, in the order the seats acted, ending on the decision
   this chart answers.

   Every action is a fixed sequence of moves, so the story falls out of the same seats and
   branch the tree was navigated with. The one thing it reads off the chart is what hero's
   raise is made to: the size is the chart's own, and the last beat is where it belongs. */

import { opponentName } from "./opponents";
import { blinds, heroRaiseSize } from "./practice";
import { seatsFor } from "./rules";
import { seatName } from "./seats";
import type { Opponent } from "./opponents";
import type { Bets, SpotPlace } from "./spot";
import type { Action } from "./hands";

const BRANCH_VERB: Readonly<Record<string, string>> = {
  Called: "Calls",
  Folded: "Folds",
  "4-Bet": "4-Bets",
  Jammed: "Jams",
};
const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/** One beat. `who` is a seat id, for whoever draws it to name; empty means nobody in
    particular acted: a fold-around. `what` is a sentence, and names seats as the user does. */
export interface Beat {
  who: string;
  what: string;
  kind: "" | "hero" | "note" | "hero now";
}
const beat = (who: string, what: string, kind: Beat["kind"] = ""): Beat => ({ who, what, kind });

/** Where the chart sits, plus what it raises and for how much, where it is known. */
export type StoriedSpot = SpotPlace & {
  bets?: Bets | undefined;
  actions?: readonly Action[] | undefined;
};

/**
 * The preflop sequence that leads to this chart, as beats.
 * Seats are in acting order and an action fixes what each of them did, so the only reading
 * is off `spot.action`; the branch, where there is one, is the answer an opponent gave the
 * last raise before hero saw it.
 *
 * A variation opens on the kind of table it is played at, before a card is dealt: it is the
 * one thing about this hand that was true before anybody acted. `opponents` is the
 * collection's list, which is where the profile's name is read from.
 */
export function spotStory(spot: StoriedSpot, opponents: readonly Opponent[] = []): Beat[] {
  const opening = spot.profile
    ? [beat("", `Against a ${opponentName(opponents, spot.profile)}`, "note")]
    : [];
  return [...opening, ...playedOut(spot)];
}

/** The beats of the hand itself, from the first action to hero's decision. */
function playedOut(spot: StoriedSpot): Beat[] {
  const order = seatsFor(spot.gameType);
  const hero = spot.hero;
  const a = spot.seats[0] ?? "";
  const b = spot.seats[1] ?? "";
  const at = (s: string): number => order.indexOf(s);
  const size = spot.openSize;

  const opens = (s: string): Beat =>
    beat(s, s === hero ? `Hero opens ${size}` : `Opens ${size}`, s === hero ? "hero" : "");
  const mine = (s: string, what: string): Beat => beat(s, `Hero ${what}`, "hero");
  // Seats that fold between two actors are not worth a row each, but the fact that the
  // action reached hero at all is.
  const gap = (from: string, to: string): boolean => at(to) - at(from) > 1;
  const folds = (s: string): Beat => beat("", `Folds to ${seatName(s)}`, "note");
  const backTo = (s: string): Beat => beat("", `Folds back to ${seatName(s)}`, "note");
  // Someone sits behind the last opponent to act, so the action has to travel back to hero
  // rather than simply being on it.
  const behind = (): boolean => at(spot.seats[spot.seats.length - 1] ?? "") < order.length - 1;
  // A chart that raises says for how much, on the beat where hero would raise. A chart with
  // no raise in it has no size to name, and neither has a spot facing a jam.
  const raise = spot.actions?.includes("raise") ? heroRaiseSize(spot) : null;
  const decide = (what?: string): Beat =>
    beat(
      hero,
      (what ?? "Hero decides") + (raise ? ` — raise to ${blinds(raise)}` : ""),
      "hero now",
    );

  switch (spot.action) {
    case "RFI":
      return [
        at(hero) ? folds(hero) : beat("", `${seatName(hero)} is first to act`, "note"),
        decide("Hero opens or folds"),
      ];
    case "vs. RFI":
      return [opens(a), ...(gap(a, hero) ? [folds(hero)] : []), decide()];
    case "vs. 3 Bet":
      return [opens(hero), beat(a, "3-bets"), ...(behind() ? [backTo(hero)] : []), decide()];
    case "vs. 4 Bet":
      return [opens(a), mine(hero, "3-bets"), beat(a, "4-bets"), decide()];
    case "vs. 5 Bet":
      return [opens(hero), beat(a, "3-bets"), mine(hero, "4-bets"), beat(a, "5-bets"), decide()];
    case "Cold 4bet":
      return [opens(a), beat(b, "3-bets"), ...(gap(b, hero) ? [folds(hero)] : []), decide()];
    case "vs. Raise-Call":
      return [opens(a), beat(b, "Calls"), ...(gap(b, hero) ? [folds(hero)] : []), decide()];
    case "vs. Squeeze":
      return [
        opens(hero),
        beat(a, "Calls"),
        beat(b, "Squeezes"),
        ...(behind() ? [backTo(hero)] : []),
        decide(),
      ];
    case "Called vs. Squeeze":
      return [
        opens(a),
        mine(hero, "calls"),
        beat(b, "Squeezes"),
        ...(spot.branch ? [beat(a, BRANCH_VERB[spot.branch] ?? cap(spot.branch))] : []),
        decide(),
      ];
  }
  // An action the app has not been taught the shape of still has a chart, and the seats it
  // names are still worth showing.
  return [...spot.seats.map((s) => beat(s, "Acts")), decide()];
}
